import { Button } from '@invent/wl-ui-kit-next/ui';
import { Download } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { DataTable, type IDataColumn } from '@/design';
import { SAVE_REFUSED, saveFile } from '@/domains/api';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import type { IPage } from '../../api/oversight-contracts';
import type { IRankingJournalEvent } from '../../api/ranking-process-contracts';
import { rankingProcessApi } from '../../api/ranking-process-api';
import { LoadError, Pager, useAdminData } from '../../components/admin-page';
import { personHubPath } from '../../components/participant-link';
import { wordFor } from '../../figures';
import { RANKING_JOURNAL_WORDS, countText } from '../../rankings';
import { refusalMessage } from '../../refusal';
import { useRanking } from './ranking';

/**
 * A ranking's History tab (SR-231, design §6.3): its journal, newest first, twenty a page — when,
 * who, what, a summary of `details` and the reason. `details` is read loosely, member by member: an
 * import's file and counts (and whether gaps were closed), an edit's place before and after and how
 * many other rows it shifted, the participant named as survey-api reads them (`participant`) and
 * linked to the hub for a reader holding `responses.browse`; a publish's counts, a cycle linked, a
 * firm's finalists file, the moment the figures were frozen at and — for a refreeze — the one it
 * moved from (SR-451). A member missing or of another shape is left out, never guessed.
 */
export function RankingHistoryTab() {
  const { key, overview } = useRanking();
  const [page, setPage] = React.useState(1);
  const version = overview?.ranking.version ?? 0;
  const { data, error } = useAdminData<{ page: number; events: IPage<IRankingJournalEvent> }>(
    async (signal) => {
      try {
        return { page, events: await rankingProcessApi.journal(key, page, signal) };
      } catch (readError) {
        throw new Error(refusalMessage(readError, 'Could not read the history'));
      }
    },
    [page, version]
  );
  const shown = data?.page === page ? data.events : null;
  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-muted-foreground">
          {shown && `${countText(shown.total)} ${shown.total === 1 ? 'event' : 'events'}`}
        </span>
        <div className="flex items-center gap-3">
          <ExportButton empty={shown?.total === 0} />
        </div>
      </div>
      <DataTable
        label="History"
        columns={COLUMNS}
        rows={shown && shown.items.length > 0 ? shown.items : null}
        rowKey={(event) => `${event.operationId}:${event.kind}:${event.at}`}
        rowProps={(event) => ({ 'data-event': event.kind }) as React.HTMLAttributes<HTMLTableRowElement>}
        maxHeight="none"
        state={
          error
            ? { kind: 'error', content: <LoadError message={error} /> }
            : shown === null
              ? { kind: 'loading' }
              : { kind: 'empty', content: 'Nothing has happened to this ranking yet.' }
        }
      />
      {shown && (
        <Pager page={shown.page} pageSize={shown.pageSize} total={shown.total} onPage={setPage} />
      )}
    </div>
  );
}

/** The whole journal as an XLSX, every page, in this tab's columns (SR-508). */
function ExportButton({ empty }: { empty: boolean }) {
  const { key } = useRanking();
  const [busy, setBusy] = React.useState(false);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const download = async () => {
    setBusy(true);
    setSaid(null);
    try {
      const { file, fileName } = await rankingProcessApi.journalFile(key);
      if (!saveFile(file, fileName ?? `${key.surveyCode}-${key.list}-${key.year}-history.xlsx`)) {
        setSaid(SAVE_REFUSED);
      }
    } catch (failure) {
      setSaid(refusalMessage(failure, 'Could not export the history'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {said && (
        <p role="alert" className="text-sm text-destructive">
          {said}
        </p>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 gap-1.5 font-normal"
        disabled={busy || empty}
        onClick={() => void download()}
      >
        <Download className="size-4" aria-hidden />
        {busy ? 'Exporting…' : 'Export'}
      </Button>
    </>
  );
}

const COLUMNS: IDataColumn<IRankingJournalEvent>[] = [
  {
    id: 'at',
    header: 'When',
    className: 'text-xs whitespace-nowrap',
    cell: (event) => formatDateTime(event.at)
  },
  { id: 'who', header: 'Who', cell: (event) => event.actor?.email ?? '—' },
  {
    id: 'kind',
    header: 'What',
    className: 'font-medium',
    cell: (event) => wordFor(RANKING_JOURNAL_WORDS, event.kind)
  },
  { id: 'details', header: 'Details', cell: (event) => <Summary event={event} /> },
  { id: 'reason', header: 'Reason', cell: (event) => event.reason ?? '' }
];

function text(value: unknown): Nullable<string> {
  return typeof value === 'string' ? value : null;
}

/** A count with its word, or `null` where the details carry none. */
function counted(value: unknown, word: string): Nullable<string> {
  const n = num(value);
  return n === null ? null : `${n} ${word}`;
}

function num(value: unknown): Nullable<number> {
  return typeof value === 'number' ? value : null;
}

function record(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

/** One side of an edit — "4 in Northern CA", "4", or "nowhere". */
function side(value: unknown): string {
  const place = num(record(value).place);
  if (place === null) return 'nowhere';
  const territory = text(record(value).territory);
  return territory ? `${place} in ${territory}` : String(place);
}

function Summary({ event }: { event: IRankingJournalEvent }) {
  const details = event.details;
  if (event.kind === 'imported') {
    const diff = record(details.diff);
    const parts = [
      text(details.fileName),
      num(details.placed) === null ? null : `${num(details.placed)} placed`,
      counted(details.territories, 'territories'),
      details.diff
        ? `+${num(diff.added) ?? 0} −${num(diff.removed) ?? 0} ↕${num(diff.moved) ?? 0} =${num(diff.unchanged) ?? 0}`
        : null,
      details.compacted === true ? 'gaps closed' : null
    ].filter(Boolean);
    return <span>{parts.join(' · ')}</span>;
  }
  if (event.kind === 'published' || event.kind === 'returned_to_draft') {
    const closed = num(details.compacted);
    return (
      <span>
        {[
          counted(details.placements, 'placed'),
          counted(details.territories, 'territories'),
          closed ? `${closed} renumbered to close gaps` : null
        ]
          .filter(Boolean)
          .join(' · ')}
      </span>
    );
  }
  if (event.kind === 'cycle_linked') {
    const cycle = text(details.cycleCode);
    // An import that moved the draft names the cycle it left (SR-415); a link by hand does not.
    const from = text(details.from);
    return (
      <span>
        {cycle ? `Cycle ${cycle}` : 'No cycle'}
        {from ? `, moved from ${from}` : ''}
      </span>
    );
  }
  if (event.kind === 'frozen') {
    // The first export's freeze names its moment; a refreeze names the one it left too (SR-451).
    const at = text(details.frozenAt);
    const from = text(details.from);
    return (
      <span>
        {at ? `Snapshot at ${formatDateTime(at)}` : 'Snapshot'}
        {from ? `, moved from ${formatDateTime(from)}` : ''}
      </span>
    );
  }
  if (event.kind === 'finalists_imported') {
    return (
      <span>
        {[
          text(details.firm),
          text(details.fileName),
          `${num(details.removed) ?? 0} removed`,
          // An answer before SR-415 counts removals alone.
          counted(details.added, 'added'),
          counted(details.confirmed, 'confirmed')
        ]
          .filter(Boolean)
          .join(' · ')}
      </span>
    );
  }
  if (event.kind === 'threshold_set') {
    return <span>{thresholdSummary(details.threshold)}</span>;
  }
  const shifted = num(details.shifted);
  return (
    <span>
      {event.participant && <PersonLink participant={event.participant} />}
      {side(details.old)} → {side(details.new)}
      {shifted !== null && ` · shifted ${shifted}`}
    </span>
  );
}

/** A threshold set (SR-315): each metric and scope at or above its minimum, how they combine,
 *  its margin and its territory bands; "Cleared" for none. */
function thresholdSummary(value: unknown): string {
  if (typeof value !== 'object' || value === null) return 'Cleared';
  const threshold = record(value);
  const conditions = (Array.isArray(threshold.conditions) ? threshold.conditions : [])
    .filter((entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null)
    .map((entry) => {
      const name = [text(entry.metric), text(entry.scope)].filter(Boolean).join(' · ');
      const min = num(entry.min);
      return min === null ? name : `${name} ≥ ${min.toLocaleString('en-US')}`;
    });
  const margin = num(threshold.marginPercent);
  const bands = Array.isArray(threshold.groups) ? threshold.groups.length : 0;
  const said = [
    conditions.length > 0 ? conditions.join(threshold.combine === 'all' ? ' and ' : ' or ') : null,
    margin === null ? null : `margin ${margin}%`,
    bands > 0 ? `${bands} territory bands` : null
  ]
    .filter(Boolean)
    .join(' · ');
  return said || 'Set';
}

/** The person an edit moved, by name — a link to the hub for a reader who opens People. */
function PersonLink({
  participant
}: {
  participant: NonNullable<IRankingJournalEvent['participant']>;
}) {
  const browses = useCan()('responses.browse');
  return (
    <>
      {browses ? (
        <Link to={personHubPath(participant.participantId)} className="underline">
          {participant.displayName}
        </Link>
      ) : (
        participant.displayName
      )}
      {': '}
    </>
  );
}
